import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Text } from '../../../../components/Text/Text';
import { LUMIO } from '../../../../theme/tokens';
import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { QaStrip } from '../../../../features/ftux/qa/QaStrip';
import { useLumioControl } from '../../lumioControl';
import { useLumioNudge } from '../../lumioNudge';
import { routeLabel, useRouteLabel } from '../../routeLabels';
import { renderNudge } from '../NudgeToast/NudgeToast';
import { isDockableNudge, proposalsPreview } from './briefing';
import { BackIcon, ChevronRightIcon, CloseIcon, DecisionIcon, MenuDotsIcon, NewChatIcon, SendIcon, StopIcon, TrayIcon } from './icons';
import { useLumioChat } from './useLumioChat';
import { usePageActions } from './pageActions';
import { SuggestionCards } from './SuggestionCards';
import { ConversationMenu } from './ConversationMenu';
import { SlotMachine } from './SlotMachine';
import { ChatMessage } from './types';
import { markdownToHtml, plainToHtml } from './markdown';
import {
  Root,
  Top,
  Header,
  Logo,
  LogoMark,
  LogoText,
  SectionTag,
  Actions,
  ActionButton,
  CloseButton,
  TrayButton,
  Badge,
  Greeting,
  Hello,
  Intro,
  IntroStrong,
  Middle,
  Sections,
  InputBlock,
  Pill,
  PromptInput,
  SendButton,
  StopButton,
  Thread,
  MessageRow,
  UserBubble,
  Assistant,
  MarkdownText,
  Typing,
  MetaNote,
  ErrorRow,
  ErrorText,
  RetryButton,
  ProposalTitle,
  ProposalSummary,
  ChipRow,
  Chip,
  BriefSection,
  Eyebrow,
  NeedsCard,
  NeedsIcon,
  NeedsBody,
  NeedsTitle,
  NeedsSub,
  NeedsChevron,
  NoticedCard,
  NoticedText,
  NoticedDismiss,
  NoticedAction,
  PageChips,
  PageChip,
  InboxHead,
  BackButton,
  InboxTitle,
  InboxCount,
  ProposalCard,
  InboxEmpty,
} from './LumioChat.styles';

/**
 * Right-hand Lumio assistant panel — Figma node 4662:343 (`lumio`).
 *
 * Two states share one frame:
 *  - **Welcome** (no messages): header, greeting, suggestion sections (HOT TOPICS
 *    / DEEP DIVE), and the prompt pill. Suggestion cards are now send shortcuts.
 *  - **Conversation** (messages exist): header (with a "new conversation" control),
 *    a scrolling message thread (user bubbles + streamed markdown replies), and
 *    the composer.
 *
 * The conversation is driven by `useLumioChat`, which talks to the Lumio chat
 * backend via `streamLumioReply` (`./endpoint`): lazy `/chat/init`, then `/chat/`
 * per turn, the reply typed out word-by-word with Stop/abort support. The section
 * tag and copy follow the current route (same map as the TopBar breadcrumb).
 */
const LOGO_SRC = '/assets/lumio/lumio-mark.svg';

/** Lumio's place for the FTUX while this panel shows: with the dock open on wide desktop the top bar's Lumio trigger is
 *  hidden, so the hello lands on this header mark instead (inert outside an FTUX step). */
const LUMIO_PLACE_TARGET: FtuxTargetId = 'lumio-place';

/** Invisible sentinel marking the live insertion point; swapped for the caret span
 * AFTER markdown rendering so the blinking cursor sits inside the last block.
 * Two U+2063 (invisible separator), vanishingly unlikely in a reply, and it passes
 * through markdownToHtml's escaper untouched. */
const CARET_TOKEN = '⁣⁣';

const assistantHtml = (content: string, streaming?: boolean): string =>
  streaming
    ? markdownToHtml(content + CARET_TOKEN).replace(CARET_TOKEN, '<span class="lc-caret"></span>')
    : markdownToHtml(content);

export const LumioChat = ({
  active = true,
  onClose,
}: { active?: boolean; onClose?: () => void } = {}): React.ReactElement => {
  const location = useLocation();
  const { t } = useTranslation();
  const sectionLabel = routeLabel(location.pathname);
  // Translated section name for display (tag + interpolation). The RAW
  // `sectionLabel` stays the stable id passed to `useLumioChat`; the displayed
  // value is translated reactively here so the closed section vocabulary is
  // harvested at this render site. i18next never translates placeholder values,
  // so we interpolate the already-translated `section` into the localized
  // sentences.
  const section = useRouteLabel(location.pathname);

  // The conversation reads the section live (reserved as assistant context).
  // `active` gates the welcome fetch so a hidden-but-mounted docked panel stays quiet.
  const {
    messages,
    status,
    isBusy,
    send,
    stop,
    reset,
    retryLast,
    resumeConversation,
    resuming,
    resumeError,
    retryResume,
    welcome,
    retryWelcome,
    pendingProposals,
    decideProposal,
  } = useLumioChat(sectionLabel, active);
  const hasConversation = messages.length > 0;

  // The proposals inbox — the dedicated screen the header tray / Needs-you card
  // opens; a plain overlay over whichever middle state is active. Auto-closes when
  // the last proposal is decided (nothing left to show).
  const [inboxOpen, setInboxOpen] = useState(false);
  const openInbox = useCallback(() => setInboxOpen(true), []);
  const closeInbox = useCallback(() => setInboxOpen(false), []);
  useEffect(() => {
    if (pendingProposals.length === 0) setInboxOpen(false);
  }, [pendingProposals.length]);

  // "On this page" chips for the current route (empty for areas with nothing
  // page-specific to offer — the briefing then omits the section).
  const pageActions = usePageActions(location.pathname);

  // The live proactive nudge, docked into the briefing as the "Noticed" card (its
  // floating toast is suppressed by MainLayout whenever a panel is showing it).
  // A `pending_decisions` nudge is skipped here: the Needs-you card already owns
  // proposals, so docking it too would double up.
  const { nudge, resolve: resolveNudge, reportSurface } = useLumioNudge();
  const nudgeCopy = nudge ? renderNudge(nudge) : null;
  const showNudge = Boolean(nudgeCopy && isDockableNudge(nudge));

  // Tell the layout whether this panel is currently a place a nudge can land.
  // Only the briefing has the "Noticed" card, so a started conversation, the
  // inbox, and the resume states all take the surface away. Without this the
  // layout would ask for nudges that nothing renders, and spend them.
  const canDockNudge = active && !hasConversation && !inboxOpen && !resuming && !resumeError;
  useEffect(() => {
    if (!canDockNudge) return undefined;
    reportSurface(1);
    return () => reportSurface(-1);
  }, [canDockNudge, reportSurface]);

  // Needs-you card subtitle: a preview of the first proposal titles (+N more) so
  // the digest hints at what's waiting without opening the inbox.
  const needsSub = proposalsPreview(pendingProposals, (count) => t('+{{count}} more', { count }));

  // The welcome suggestion cards ship gated: the greeting always shows, the
  // suggestions only when the `lumio-suggestions` flag is on (revealed live via
  // `show_in_progress`). Default is just the "Hello" welcome.
  const { isEnabled } = useFeatureGate();
  const showSuggestions = isEnabled('lumio-suggestions');

  // A page asked Lumio to "tell me more": send that seed as the first turn — but
  // ONLY on the active surface (this same panel is hidden-but-mounted in the
  // docked column off its breakpoint, where the drawer owns Lumio, so it must
  // not also fire). Two guards: the shared `consumedNonce` makes the send
  // at-most-once across all surfaces/remounts; the per-instance ref additionally
  // blocks a same-instance re-run (StrictMode double-invoke, `send` re-identity).
  const { seed, consumedNonce, consumeSeed, impersonating = false } = useLumioControl();
  const seededNonceRef = useRef(0);
  useEffect(() => {
    if (!active || !seed || seed.nonce === seededNonceRef.current || seed.nonce <= consumedNonce) return;
    seededNonceRef.current = seed.nonce;
    consumeSeed(seed.nonce);
    setInboxOpen(false); // a seeded turn supersedes the inbox: show the reply
    send(seed.text);
  }, [active, seed, consumedNonce, send, consumeSeed]);

  // Past-conversations flyout (the header "…" button).
  const [historyOpen, setHistoryOpen] = useState(false);
  const dotsRef = useRef<HTMLButtonElement>(null);
  const closeHistory = useCallback(() => setHistoryOpen(false), []);
  const selectConversation = useCallback(
    (conversationId: string) => {
      resumeConversation(conversationId);
      setHistoryOpen(false);
    },
    [resumeConversation],
  );

  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  // The scrolling middle (welcome greeting + suggestions, or the conversation Thread)
  // sits in the same grid cell as the header/composer and scrolls UNDER them, so its
  // top/bottom padding (and the fade mask) must track their live heights — the composer
  // grows with the textarea, and the footer loses its label between states, so this is
  // measured, not hard-coded. Runs in BOTH states (the welcome panel scrolls too).
  const [chrome, setChrome] = useState({ header: 0, footer: 0 });
  useLayoutEffect(() => {
    const measure = (): void =>
      setChrome({
        header: headerRef.current?.offsetHeight ?? 0,
        footer: footerRef.current?.offsetHeight ?? 0,
      });
    measure();
    const observer = new ResizeObserver(measure);
    if (headerRef.current) observer.observe(headerRef.current);
    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, [hasConversation]);

  // Auto-grow the composer to fit its content (capped by the CSS max-height).
  // Guarded for the hidden-mount case: the docked Lumio column is `display:none`
  // below 1280px, so this panel can mount while it has no layout boxes. Reading
  // scrollHeight then is 0, which would pin the textarea to `height:0px` — and,
  // because the original resize keyed only on `input`, it would STAY collapsed
  // (invisible) once the column was revealed. So we (a) never measure while the
  // textarea is detached from layout (`offsetParent === null`), and (b) also
  // re-measure whenever its width changes — which fires when it goes hidden →
  // visible (column reveal / drawer paint). A CSS `min-height` floor on
  // PromptInput is the belt-and-braces guarantee it can never render invisible.
  const autosize = useCallback(() => {
    const el = textareaRef.current;
    if (!el || el.offsetParent === null) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    autosize();
  }, [input, autosize]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return undefined;
    let lastWidth = el.offsetWidth;
    const observer = new ResizeObserver(() => {
      // React only to WIDTH changes (visibility toggle / column resize), never to
      // our own height writes — otherwise the observer would feed back on itself.
      const width = el.offsetWidth;
      if (width !== lastWidth) {
        lastWidth = width;
        autosize();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [autosize]);

  // Keep the newest message in view as turns arrive / stream.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, status]);

  const submit = useCallback(
    (text: string) => {
      if (!text.trim() || isBusy) return;
      setInboxOpen(false); // leaving the inbox so the reply is visible in the thread
      send(text);
      setInput('');
    },
    [isBusy, send],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        submit(input);
      } else if (event.key === 'Escape' && isBusy) {
        // Escape cancels the in-flight reply. Stop it bubbling so a host that also
        // listens for Escape (e.g. the LumioDrawer, which closes on Escape) doesn't
        // tear the whole panel down in the same keypress — cancel first, close on a
        // second Escape.
        event.stopPropagation();
        stop();
      }
    },
    [input, isBusy, stop, submit],
  );

  const renderAssistant = (message: ChatMessage): React.ReactElement => {
    const thinking = message.streaming && message.content === '';
    return (
      <Assistant>
        {message.slot ? <SlotMachine result={message.slot} /> : null}
        {thinking ? (
          <Typing aria-label={t('Lumio is thinking')}>
            <span />
            <span />
            <span />
          </Typing>
        ) : message.content ? (
          <MarkdownText dangerouslySetInnerHTML={{ __html: assistantHtml(message.content, message.streaming) }} />
        ) : null}
        {message.stopped && <MetaNote text="Stopped" />}
        {message.error && (
          <ErrorRow>
            <ErrorText text="Lumio couldn’t respond. Please try again." />
            <RetryButton type="button" onClick={retryLast}>
              <Text text="Retry" />
            </RetryButton>
          </ErrorRow>
        )}
      </Assistant>
    );
  };

  return (
    <Root>
      <Top ref={headerRef}>
        <Header>
          <Logo>
            <LogoMark data-ftux={LUMIO_PLACE_TARGET}>
              {/* Multicolour gradient mark — not recoloured → plain <img>, no fetch-and-inline. */}
              <img src={LOGO_SRC} alt="" />
            </LogoMark>
            <LogoText text="Lumio" />
            {/* Route-driven section name from the closed UI vocabulary —
                translated at the render site (see `section` above). */}
            <SectionTag text={section} />
          </Logo>
          <Actions>
            {/* Proposals tray — an unread-mail-style entry to the inbox. Shown
             * only when something is pending (no dead tray in player mode); the
             * count badge and the inbox both clear as decisions are made. Stays
             * put mid-conversation so proposals are always one click away. */}
            {pendingProposals.length > 0 && !inboxOpen && (
              <TrayButton
                type="button"
                onClick={openInbox}
                aria-label={t('Proposals')}
                title={t('Proposals')}
              >
                <TrayIcon />
                <Badge aria-hidden="true">{pendingProposals.length}</Badge>
              </TrayButton>
            )}
            {/* "New conversation" (compose): shown only mid-conversation, since the
             * welcome state is already a fresh start. */}
            {hasConversation && (
              <ActionButton
                type="button"
                onClick={reset}
                aria-label={t('New conversation')}
                title={t('New conversation')}
              >
                <NewChatIcon size={22} />
              </ActionButton>
            )}
            {/* "…" opens the past-conversations flyout. */}
            <ActionButton
              ref={dotsRef}
              type="button"
              onClick={() => setHistoryOpen((v) => !v)}
              aria-label={t('Past conversations')}
              title={t('Past conversations')}
              aria-haspopup="menu"
              aria-expanded={historyOpen}
            >
              <MenuDotsIcon size={22} />
            </ActionButton>
            {/* Close: hides the panel (the docked column or the drawer); the top-bar trigger brings it back. */}
            {onClose ? (
              <CloseButton type="button" onClick={onClose} aria-label={t('Close Lumio')} title={t('Close Lumio')}>
                <CloseIcon />
              </CloseButton>
            ) : null}
          </Actions>
        </Header>
      </Top>

      {inboxOpen ? (
        // Proposals inbox — a dedicated screen over whatever middle state is
        // active, reached from the header tray / Needs-you card. Deciding the last
        // one empties `pendingProposals`, which closes the inbox (effect above).
        <Middle $headerH={chrome.header} $footerH={chrome.footer}>
          <InboxHead>
            <BackButton type="button" onClick={closeInbox} aria-label={t('Back')}>
              <BackIcon />
            </BackButton>
            <InboxTitle text="Proposals" />
            {pendingProposals.length > 0 && (
              <InboxCount text="{{count}} waiting" placeholders={{ count: pendingProposals.length }} />
            )}
          </InboxHead>
          {pendingProposals.length === 0 ? (
            <InboxEmpty text="You’re all caught up. No proposals waiting." />
          ) : (
            pendingProposals.map((proposal) => {
              // The first accept action is the primary CTA; other accepts are
              // secondary, deny is muted — so multi-action proposals read with a
              // clear hierarchy (see the Chip tiers in the styles).
              const primaryId = proposal.actions.find((a) => a.decision === 'accept')?.id;
              return (
                <ProposalCard key={proposal.proposal_id}>
                  <ProposalTitle text={proposal.title} />
                  {proposal.summary && <ProposalSummary text={proposal.summary} />}
                  <ChipRow>
                    {proposal.actions.map((action) => (
                      <Chip
                        key={action.id}
                        type="button"
                        $deny={action.decision === 'deny'}
                        $primary={action.decision === 'accept' && action.id === primaryId}
                        title={action.note || undefined}
                        disabled={isBusy}
                        onClick={() => decideProposal(proposal.proposal_id, action.id)}
                      >
                        {action.label}
                      </Chip>
                    ))}
                  </ChipRow>
                </ProposalCard>
              );
            })
          )}
        </Middle>
      ) : resumeError ? (
        // Loading a past conversation failed: an honest error + retry (never a
        // silent no-op or a stale thread).
        <Middle $headerH={chrome.header} $footerH={chrome.footer}>
          <ErrorRow>
            <ErrorText text="Couldn’t open that conversation." />
            <RetryButton type="button" onClick={retryResume}>
              <Text text="Retry" />
            </RetryButton>
          </ErrorRow>
        </Middle>
      ) : resuming ? (
        <Middle $headerH={chrome.header} $footerH={chrome.footer}>
          <Typing aria-label={t('Loading conversation')}>
            <span />
            <span />
            <span />
          </Typing>
        </Middle>
      ) : hasConversation ? (
        <Thread ref={threadRef} $headerH={chrome.header} $footerH={chrome.footer}>
          {messages.map((message) => (
            <MessageRow key={message.id} $role={message.role}>
              {message.role === 'user' ? (
                <UserBubble dangerouslySetInnerHTML={{ __html: plainToHtml(message.content) }} />
              ) : (
                renderAssistant(message)
              )}
            </MessageRow>
          ))}
        </Thread>
      ) : (
        // Briefing: the idle "for you" digest — greeting, the proposals digest
        // (Needs you), the docked nudge (Noticed), route-aware page chips, and the
        // gated suggestion cards. Each section renders only when it has something.
        <Middle $headerH={chrome.header} $footerH={chrome.footer}>
          <Greeting>
            {/* Name-less until a profile lands in the store (no fake names in
             * shipped copy) — switch to `Hello {{name}} 👋🏼` when it does. */}
            <Hello text="Hello 👋🏼" />
            <Intro>
              <Text text="I’m" /> <IntroStrong text="Lumio" />
              <Text text=", your personal AI business partner." />
            </Intro>
          </Greeting>

          {pendingProposals.length > 0 && (
            <BriefSection>
              <Eyebrow $color={LUMIO.hotTopics}>{t('Needs you')}</Eyebrow>
              <NeedsCard type="button" onClick={openInbox}>
                <NeedsIcon aria-hidden="true">
                  <DecisionIcon />
                </NeedsIcon>
                <NeedsBody>
                  <NeedsTitle
                    text="You have {{count}} decisions waiting on you."
                    placeholders={{ count: pendingProposals.length }}
                  />
                  {needsSub && <NeedsSub text={needsSub} />}
                </NeedsBody>
                <NeedsChevron aria-hidden="true">
                  <ChevronRightIcon />
                </NeedsChevron>
              </NeedsCard>
            </BriefSection>
          )}

          {showNudge && nudgeCopy && (
            <BriefSection>
              <Eyebrow $color={LUMIO.link}>{t('Noticed')}</Eyebrow>
              <NoticedCard>
                <NoticedDismiss type="button" onClick={() => resolveNudge('dismissed')} aria-label={t('Dismiss')}>
                  ×
                </NoticedDismiss>
                <NoticedText text={nudgeCopy.message} />
                {nudge?.action && nudgeCopy.action && (
                  <NoticedAction
                    type="button"
                    onClick={() => {
                      const message = nudgeCopy.action!.message;
                      resolveNudge('clicked');
                      submit(message);
                    }}
                  >
                    {nudgeCopy.action.label}
                  </NoticedAction>
                )}
              </NoticedCard>
            </BriefSection>
          )}

          {pageActions.length > 0 && (
            <BriefSection>
              <Eyebrow $color={LUMIO.deepDive}>{t('On this page')}</Eyebrow>
              <PageChips>
                {pageActions.map((pageAction) => (
                  <PageChip key={pageAction.id} type="button" onClick={() => submit(pageAction.message)}>
                    {pageAction.label}
                  </PageChip>
                ))}
              </PageChips>
            </BriefSection>
          )}

          {showSuggestions && (
            <Sections>
              <SuggestionCards
                topics={welcome.topics}
                loading={welcome.loading}
                error={welcome.error}
                onRetry={retryWelcome}
                onPick={submit}
              />
            </Sections>
          )}
        </Middle>
      )}

      <InputBlock ref={footerRef}>
        {/* FTUX testers' strip (777ftuxqa): over the input, in the block whose measured height pads the middle. */}
        <QaStrip impersonating={impersonating} />
        <Pill>
          <PromptInput
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('Ask me anything else …')}
            aria-label={t('Message Lumio')}
          />
          {isBusy ? (
            <StopButton type="button" onClick={stop} aria-label={t('Stop generating')}>
              <StopIcon />
            </StopButton>
          ) : (
            <SendButton
              type="button"
              onClick={() => submit(input)}
              disabled={!input.trim() || resuming}
              aria-label={t('Send message')}
            >
              <SendIcon size={24} />
            </SendButton>
          )}
        </Pill>
      </InputBlock>

      <ConversationMenu open={historyOpen} anchorRef={dotsRef} onClose={closeHistory} onSelect={selectConversation} />
    </Root>
  );
};

export default LumioChat;
